/**
 * Unified AI Service
 * Provides a single interface for all AI operations with provider abstraction
 */

import {
  AIService as IAIService,
  AIProvider,
  AIModel,
  Message,
  ChatOptions,
  ChatResponse,
  CompletionOptions,
  CompletionResponse,
  ToolRequest,
  ToolResponse,
  AIConfig,
  AIError,
  AIErrorType,
} from './types';
import { providerRegistry } from './provider-registry';
import { configManager } from './config';
import { OpenAIProvider } from './providers/openai-provider';
import { AnthropicProvider } from './providers/anthropic-provider';
import { XAIProvider } from './providers/xai-provider';

export class AIService implements IAIService {
  private currentProviderId: string | null = null;
  private currentModelId: string | null = null;

  constructor(skipDefaultRegistration = false) {
    // Register default providers unless skipped (for testing)
    if (!skipDefaultRegistration) {
      this.registerDefaultProviders();
    }
    
    // Set initial provider from config
    const config = configManager.getConfig();
    this.currentProviderId = config.defaultProvider;
    this.currentModelId = config.defaultModel || null;
  }

  private registerDefaultProviders(): void {
    // Always register all providers - API keys will be checked when making requests
    providerRegistry.register(new OpenAIProvider());
    providerRegistry.register(new AnthropicProvider());
    providerRegistry.register(new XAIProvider());
  }

  private getProvider(): AIProvider {
    if (!this.currentProviderId) {
      throw new AIError(
        AIErrorType.CONFIGURATION_ERROR,
        'No AI provider selected'
      );
    }

    const provider = providerRegistry.get(this.currentProviderId);
    if (!provider) {
      throw new AIError(
        AIErrorType.PROVIDER_NOT_FOUND,
        `Provider ${this.currentProviderId} not found`
      );
    }

    return provider;
  }

  private getModelId(options?: { model?: string }): string | undefined {
    return options?.model || this.currentModelId || undefined;
  }

  async chat(messages: Message[], options?: ChatOptions): Promise<ChatResponse> {
    const provider = this.getProvider();
    const modelId = this.getModelId(options);
    
    try {
      return await provider.chat(messages, {
        ...options,
        model: modelId,
      });
    } catch (error) {
      this.handleProviderError(error);
      throw error;
    }
  }

  async complete(prompt: string, options?: CompletionOptions): Promise<CompletionResponse> {
    const provider = this.getProvider();
    const modelId = this.getModelId(options);
    
    try {
      const result = await provider.generateText({
        prompt,
        model: modelId,
        temperature: options?.temperature,
        maxTokens: options?.maxTokens,
        stopSequences: options?.stopSequences,
      });

      return {
        text: result.text,
        model: result.model,
        usage: result.usage,
        finishReason: result.finishReason === 'tool_calls' ? 'stop' : result.finishReason,
      };
    } catch (error) {
      this.handleProviderError(error);
      throw error;
    }
  }

  async *streamChat(messages: Message[], options?: ChatOptions): AsyncIterableIterator<ChatResponse> {
    const provider = this.getProvider();
    const modelId = this.getModelId(options);
    
    try {
      yield* provider.streamChat(messages, {
        ...options,
        model: modelId,
      });
    } catch (error) {
      this.handleProviderError(error);
      throw error;
    }
  }

  async *streamComplete(prompt: string, options?: CompletionOptions): AsyncIterableIterator<string> {
    const provider = this.getProvider();
    const modelId = this.getModelId(options);
    
    try {
      const stream = provider.streamText({
        prompt,
        model: modelId,
        temperature: options?.temperature,
        maxTokens: options?.maxTokens,
        stopSequences: options?.stopSequences,
      });

      for await (const chunk of stream) {
        yield chunk.text;
      }
    } catch (error) {
      this.handleProviderError(error);
      throw error;
    }
  }

  async executeTools(request: ToolRequest): Promise<ToolResponse> {
    const provider = this.getProvider();
    const modelId = this.getModelId(request);
    
    try {
      return await provider.executeTools({
        ...request,
        model: modelId,
      });
    } catch (error) {
      this.handleProviderError(error);
      throw error;
    }
  }

  configure(config: Partial<AIConfig>): void {
    configManager.updateConfig(config);
    
    // Re-register providers if API keys changed
    if (config.providers) {
      providerRegistry.clear();
      
      // Always register all providers - API keys will be checked when making requests
      providerRegistry.register(new OpenAIProvider());
      providerRegistry.register(new AnthropicProvider());
      providerRegistry.register(new XAIProvider());
    }
    
    // Update current provider/model if changed
    if (config.defaultProvider) {
      this.currentProviderId = config.defaultProvider;
    }
    if (config.defaultModel !== undefined) {
      this.currentModelId = config.defaultModel;
    }
  }

  getConfig(): AIConfig {
    return configManager.getConfig();
  }

  switchProvider(providerId: string): void {
    const provider = providerRegistry.get(providerId);
    if (!provider) {
      throw new AIError(
        AIErrorType.PROVIDER_NOT_FOUND,
        `Provider ${providerId} not found`
      );
    }
    
    this.currentProviderId = providerId;
    this.currentModelId = null; // Reset model selection
  }

  switchModel(modelId: string): void {
    const provider = this.getProvider();
    const model = provider.getModelInfo(modelId);
    
    if (!model) {
      throw new AIError(
        AIErrorType.MODEL_NOT_FOUND,
        `Model ${modelId} not found in provider ${this.currentProviderId}`,
        this.currentProviderId || undefined
      );
    }
    
    this.currentModelId = modelId;
  }

  getCurrentProvider(): AIProvider {
    return this.getProvider();
  }

  getCurrentModel(): AIModel {
    const provider = this.getProvider();
    const modelId = this.currentModelId || provider.models[0]?.id;
    
    if (!modelId) {
      throw new AIError(
        AIErrorType.MODEL_NOT_FOUND,
        'No model selected or available',
        this.currentProviderId || undefined
      );
    }
    
    const model = provider.getModelInfo(modelId);
    if (!model) {
      throw new AIError(
        AIErrorType.MODEL_NOT_FOUND,
        `Model ${modelId} not found`,
        this.currentProviderId || undefined
      );
    }
    
    return model;
  }

  listProviders(): AIProvider[] {
    return providerRegistry.list();
  }

  listModels(): AIModel[] {
    try {
      const provider = this.getProvider();
      return provider.listModels();
    } catch {
      // Return all models from all providers if no provider selected
      return providerRegistry.list().flatMap(p => p.listModels());
    }
  }

  serialize(): string {
    const state = {
      currentProviderId: this.currentProviderId,
      currentModelId: this.currentModelId,
      config: configManager.getConfig(),
    };
    return JSON.stringify(state);
  }

  deserialize(data: string): void {
    try {
      const state = JSON.parse(data);
      
      if (state.config) {
        this.configure(state.config);
      }
      
      if (state.currentProviderId) {
        this.currentProviderId = state.currentProviderId;
      }
      
      if (state.currentModelId) {
        this.currentModelId = state.currentModelId;
      }
    } catch (error) {
      throw new AIError(
        AIErrorType.CONFIGURATION_ERROR,
        'Failed to deserialize AI service state',
        undefined,
        undefined,
        error
      );
    }
  }

  async validateConfiguration(): Promise<{ valid: boolean; errors: string[] }> {
    const validation = configManager.validateConfig();
    const errors = [...validation.errors];
    
    // Additionally validate that providers can connect
    const providers = providerRegistry.list();
    for (const provider of providers) {
      try {
        const isValid = await provider.validateApiKey();
        if (!isValid) {
          errors.push(`Invalid API key for provider ${provider.id}`);
        }
      } catch (error) {
        errors.push(`Failed to validate provider ${provider.id}: ${error}`);
      }
    }
    
    return {
      valid: errors.length === 0,
      errors,
    };
  }

  estimateCost(tokens: { input: number; output: number }): number | undefined {
    try {
      const model = this.getCurrentModel();
      if (!model.costPer1kTokens) {
        return undefined;
      }
      
      const inputCost = (tokens.input / 1000) * model.costPer1kTokens.input;
      const outputCost = (tokens.output / 1000) * model.costPer1kTokens.output;
      
      return inputCost + outputCost;
    } catch {
      return undefined;
    }
  }

  private handleProviderError(error: any): void {
    // Log errors for debugging
    console.error('AI Service Error:', error);
    
    // Could add telemetry, retry logic, or fallback providers here
  }
}

// Export singleton instance with lazy initialization
let _aiService: AIService | null = null;

export const aiService = {
  get instance(): AIService {
    if (!_aiService) {
      _aiService = new AIService();
    }
    return _aiService;
  },
  
  // Proxy all methods to the instance
  chat: (messages: Message[], options?: ChatOptions) => 
    aiService.instance.chat(messages, options),
  
  complete: (prompt: string, options?: CompletionOptions) => 
    aiService.instance.complete(prompt, options),
  
  streamChat: (messages: Message[], options?: ChatOptions) => 
    aiService.instance.streamChat(messages, options),
  
  streamComplete: (prompt: string, options?: CompletionOptions) => 
    aiService.instance.streamComplete(prompt, options),
  
  executeTools: (request: ToolRequest) => 
    aiService.instance.executeTools(request),
  
  configure: (config: Partial<AIConfig>) => 
    aiService.instance.configure(config),
  
  getConfig: () => 
    aiService.instance.getConfig(),
  
  switchProvider: (providerId: string) => 
    aiService.instance.switchProvider(providerId),
  
  switchModel: (modelId: string) => 
    aiService.instance.switchModel(modelId),
  
  getCurrentProvider: () => 
    aiService.instance.getCurrentProvider(),
  
  getCurrentModel: () => 
    aiService.instance.getCurrentModel(),
  
  listProviders: () => 
    aiService.instance.listProviders(),
  
  listModels: () => 
    aiService.instance.listModels(),
  
  serialize: () => 
    aiService.instance.serialize(),
  
  deserialize: (data: string) => 
    aiService.instance.deserialize(data),
  
  validateConfiguration: () => 
    aiService.instance.validateConfiguration(),
  
  estimateCost: (tokens: { input: number; output: number }) => 
    aiService.instance.estimateCost(tokens),
};